import React, { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import FormattedDateTime from '../../../common/datetime/FormattedDateTime';
import { SalvLabel, SalvTable, SalvTablePaginationType } from '@salvapp/ui-components';
import { InternalApiScenarioTestResult } from '../../../api-internal';
import { testRunStatusColorVariant } from '../../../common/versioning/helper';
import { useNavigate, useParams } from 'react-router';
import { formatEnumLabel } from '../../../common/helper';
import { renderSampleTimeRange } from './helper';

export interface TestRunsProps {
  data: InternalApiScenarioTestResult[];
  total: number;
  isFetching: boolean;
  fetchNextPage: () => void;
}

export const DEFAULT_TEST_RUNS_PAGE_SIZE = 20;

export const TestRuns = ({ data, isFetching, fetchNextPage, total }: TestRunsProps): React.ReactElement | null => {
  const navigate = useNavigate();
  const { handle } = useParams<{ handle: string }>();

  const columnHelper = createColumnHelper<InternalApiScenarioTestResult>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('scenarioVersionId', {
        header: 'Version ID',
      }),
      columnHelper.accessor('status', {
        header: 'Status',

        cell: ({ getValue }) => (
          <SalvLabel variant={testRunStatusColorVariant(getValue())}>{formatEnumLabel(getValue())}</SalvLabel>
        ),
      }),
      columnHelper.display({
        header: 'Sample time range',
        cell: ({ row: { original: testResult } }) => {
          return renderSampleTimeRange(testResult);
        },
      }),
      columnHelper.accessor('startedTime', {
        header: 'Execution start',
        cell: ({ getValue }) => {
          const dateValue = getValue();
          if (!dateValue) {
            return '-';
          }
          return <FormattedDateTime date={getValue()} showTime />;
        },
      }),
      columnHelper.accessor('note', { header: 'Note' }),
    ],
    []
  );

  return (
    <SalvTable
      data={data}
      columns={columns}
      isLoading={isFetching}
      rowClick={(row, e) => {
        const testRun = row.original;
        const link = `/scenarios/${handle}/content/${testRun.scenarioContentHandle}/test-run/${testRun.testExecutionHandle}`;

        if (e && (e.metaKey || e.ctrlKey)) {
          window.open(link, '_blank');
        } else {
          navigate(link);
        }
      }}
      pagination={
        data && total > DEFAULT_TEST_RUNS_PAGE_SIZE
          ? {
              type: SalvTablePaginationType.LOAD_MORE,
              total: total,
              state: {
                pageIndex: data ? Math.ceil(data.length / DEFAULT_TEST_RUNS_PAGE_SIZE) - 1 : 0,
                pageSize: DEFAULT_TEST_RUNS_PAGE_SIZE,
              },
              setPagination: () => fetchNextPage(),
            }
          : undefined
      }
      className='m-t-16'
      emptyMessage='No test runs found.'
    />
  );
};

export default TestRuns;
